/**
 * iriModel.ts
 *
 * MEPDG / AASHTOWare Pavement ME JPCP IRI model, ported to the browser to sit
 * on top of the PittRigid ME engine.  It consumes the per-year cracking and
 * faulting series that `usePittRigid` already produces and adds the two
 * remaining distress terms (joint spalling and the site factor) to assemble
 * the IRI development curve.
 *
 *   IRI = IRI_i + C1·CRK + C2·SPALL + C3·TFAULT + C4·SF
 *
 *   SF    = AGE·(1 + 0.5556·FI)·(1 + P200)·1e-6
 *   SCF   = -1400 + 350·ACPCC·(0.5 + PREFORM) + 43.4·f'c^0.4
 *           - 0.2·(FTcycles·AGE) + 43·hPCC - 536·(W/C)
 *   SPALL = AGE/(AGE + 0.01) · 100/(1 + 1.005^(-12·AGE + SCF))
 *   TFAULT = fault_per_joint[in] · 5280 / jointSpacing[ft]      (in/mi)
 *
 * Units: IRI and TFAULT in in/mi, AGE in years, CRK and SPALL in %, f'c in psi,
 * FI in °F-days, FTcycles in cycles/yr, hPCC in inches.
 *
 * Verification: reproduces IRI.xlsx (sheet C33B1P1M1) to ~1e-13 in/mi over the
 * full design horizon.  (The spreadsheet's stored spalling column freezes at a
 * constant beyond ~25 yr, which is a paste artifact outside the design window;
 * this module computes spalling live, which is the correct behaviour.)
 *
 * The values below are NOT user inputs.  They are model constants that the
 * engineer maintains here in one place.
 */

import type { PittRigidResults } from './usePittRigid';

/** Only the run settings the IRI model actually needs from the inputs. */
export interface IriRunOpts {
  iClimate: number;     // 0..4, selects climate-dependent constants
  jpointspace: number;  // 0 -> 12 ft, 1 -> 15 ft
  pccMR28: number;      // 28-day modulus of rupture, psi (drives f'c for spalling)
}

// MEPDG Level-3 flexural↔compressive correlation:  MR = 9.5·√f'c  (psi)
//   →  f'c = (MR / 9.5)²
// Verified: IRI.xlsx case used MR = 500 psi → f'c = (500/9.5)² = 2770.083 psi,
// matching the spreadsheet to all digits. (Not to be confused with the ACI
// elastic-modulus relation E = 57000·√f'c, which uses 57000, not 9.5.)
const MR_FC_COEFF = 9.5;

// Joint spacing options must mirror JOINT_SPACINGS in PittRigidPage.tsx:
//   jpointspace = 0 -> 12 ft, 1 -> 15 ft
const JOINT_SPACING_FT = [12, 15] as const;

// Spalling is frozen at its value at this age. The spalling logistic keeps
// climbing with age, but the reference model holds it constant past this point
// (verified against IRI.xlsx: spalling caps at the 25-yr value and stays there
// for the rest of the design life). Both the SCF age term and the logistic age
// term use the capped age.
const SPALL_CAP_AGE_YR = 25.0;

// ─── Constants block ────────────────────────────────────────────────────────
export interface IriConstants {
  // IRI regression coefficients (MEPDG global calibration, in/mi)
  c1: number;          // transverse cracking term  (× % cracked slabs)
  c2: number;          // joint spalling term        (× % joints spalled)
  c3: number;          // faulting term              (× total faulting, in/mi)
  c4: number;          // site-factor term           (× SF)
  iriInitial: number;  // initial IRI after construction, in/mi

  // Site factor  SF = AGE·(1 + fiCoeff·FI)·(1 + P200)·1e-6
  fiCoeff: number;     // 0.5556 (fixed)
  p200: number;        // % of subgrade soil passing the #200 sieve

  // Transverse joint spalling (SCF model). f'c is NOT stored here — it is
  // derived per run from the user's modulus of rupture via MR_FC_COEFF.
  acPCC: number;       // PCC air content, %
  preform: number;     // preformed joint sealant present? 1 = yes, 0 = no
  wcPCC: number;       // water/cement ratio

  // Climate-dependent terms, indexed by opts.iClimate (0..4), mapping to the
  // five PittRigid / PennDOT regions (C1..C5 in the source database):
  //   0 = Region 1 (Erie Co.)        1 = Region 2 (D1 ex-Erie, D10-12)
  //   2 = Region 3 (D2, D9)          3 = Region 4 (D3, D4)
  //   4 = Region 5 (D5, D6, D8)
  //   freezeIndex : mean annual freezing index, °F-days
  //   ftCycles    : mean annual air freeze-thaw cycles
  freezeIndex: number[];
  ftCycles: number[];
}

export const IRI_CONSTANTS: IriConstants = {
  c1: 0.8203,
  c2: 0.4417,
  c3: 1.4929,
  c4: 25.24,
  iriInitial: 64.0,        // initial as-constructed IRI, in/mi (PennDOT value)

  fiCoeff: 0.5556,
  p200: 63.2,              // matches the p200 already hardcoded in usePittRigid faultCalc1

  acPCC: 6.0,
  preform: 0,
  wcPCC: 0.45,

  //                  C1 (R1)            C2 (R2)            C3 (R3)            C4 (R4)            C5 (R5)
  freezeIndex: [ 615.719794681266, 500.468206883066, 622.52484356668,  684.740188697693, 328.230762612437 ],
  ftCycles:    [ 32.9396362925303, 67.7362338678138, 61.1940164254986, 67.7933711380524, 62.6795854517012 ],
};

// ─── Per-year IRI result ────────────────────────────────────────────────────
export interface IriYear {
  age: number;          // years (1..iLife)
  crack50: number;      // % cracked slabs, 50% reliability
  crackRel: number;     // % cracked slabs, target reliability
  fault50: number;      // mean joint faulting, in, 50% reliability
  faultRel: number;     // mean joint faulting, in, target reliability
  spall: number;        // joints spalled, %
  scf: number;          // spalling computation factor
  sf: number;           // site factor
  tfault50: number;     // total faulting, in/mi, 50% reliability
  tfaultRel: number;    // total faulting, in/mi, target reliability
  iri50: number;        // IRI, in/mi, 50% reliability
  iriRel: number;       // IRI, in/mi, target reliability
  // Contributions to the 50%-reliability IRI (sum = iri50) for stacked plots
  cInitial: number;
  cCrack: number;
  cSpall: number;
  cFault: number;
  cSF: number;
}

export interface IriSeries {
  years: IriYear[];
  jointSpacingFt: number;
  hPCC: number;
  fcPCC: number;        // 28-day f'c derived from MR, psi
  constants: IriConstants;
}

/** Spalling computation factor (SCF) at a given age. */
function scfAt(ageYr: number, hPCC: number, fcPCC: number, k: IriConstants, climate: number): number {
  const ft = k.ftCycles[climate] ?? k.ftCycles[0];
  return (
    -1400
    + 350 * k.acPCC * (0.5 + k.preform)
    + 43.4 * fcPCC ** 0.4
    - 0.2 * (ft * ageYr)
    + 43 * hPCC
    - 536 * k.wcPCC
  );
}

/** % joints spalled at a given age (uses the SCF above). */
function spallAt(ageYr: number, scf: number): number {
  return (ageYr / (ageYr + 0.01)) * (100 / (1 + 1.005 ** (-12 * ageYr + scf)));
}

/** Site factor at a given age. */
function sfAt(ageYr: number, k: IriConstants, climate: number): number {
  const fi = k.freezeIndex[climate] ?? k.freezeIndex[0];
  return ageYr * (1 + k.fiCoeff * fi) * (1 + k.p200) * 1e-6;
}

/**
 * Build the full per-year IRI series from a completed PittRigid run.
 *
 * Reliability propagation: IRI at the target reliability uses the cracking and
 * faulting series already adjusted to that reliability; spalling and the site
 * factor have no reliability bound in this model, so they are shared between
 * the 50% and reliability curves (standard component-propagation approach).
 */
export function computeIriSeries(
  opts: IriRunOpts,
  res: PittRigidResults,
  k: IriConstants = IRI_CONSTANTS,
): IriSeries {
  const jointSpacingFt = JOINT_SPACING_FT[opts.jpointspace] ?? 15;
  const hPCC = res.hPCC;
  const fcPCC = (opts.pccMR28 / MR_FC_COEFF) ** 2;   // f'c = (MR/9.5)²
  const n = res.crackAge.length; // = iLife
  const years: IriYear[] = [];

  for (let i = 0; i < n; i++) {
    const age = i + 1; // end of design year i+1

    const crack50  = res.crackAge[i];
    const crackRel = res.crackAgeRel[i];
    const fault50  = res.fault[i];
    const faultRel = res.faultRel[i];

    // Spalling freezes at its SPALL_CAP_AGE_YR value; the site factor and
    // faulting keep growing with the true age.
    const spallAge = Math.min(age, SPALL_CAP_AGE_YR);
    const scf = scfAt(spallAge, hPCC, fcPCC, k, opts.iClimate);
    const spall = spallAt(spallAge, scf);
    const sf = sfAt(age, k, opts.iClimate);

    const tfault50  = (fault50  * 5280) / jointSpacingFt;
    const tfaultRel = (faultRel * 5280) / jointSpacingFt;

    const cInitial = k.iriInitial;
    const cCrack   = k.c1 * crack50;
    const cSpall   = k.c2 * spall;
    const cFault   = k.c3 * tfault50;
    const cSF      = k.c4 * sf;

    const iri50  = cInitial + cCrack + cSpall + cFault + cSF;
    const iriRel = k.iriInitial + k.c1 * crackRel + k.c2 * spall + k.c3 * tfaultRel + k.c4 * sf;

    years.push({
      age, crack50, crackRel, fault50, faultRel,
      spall, scf, sf, tfault50, tfaultRel, iri50, iriRel,
      cInitial, cCrack, cSpall, cFault, cSF,
    });
  }

  return { years, jointSpacingFt, hPCC, fcPCC, constants: k };
}

// ─── Temporary verification CSV (Phase 1 — remove once validated) ────────────
/**
 * Builds a CSV string with every IRI intermediate so the numbers can be checked
 * against IRI.xlsx column-by-column.  Wire `downloadIriCsv` to a button, run a
 * design, then diff against the spreadsheet.  Delete this block afterwards.
 */
export function buildIriCsv(series: IriSeries): string {
  const k = series.constants;
  const meta = [
    `# PittRigid ME — IRI verification export`,
    `# jointSpacingFt,${series.jointSpacingFt}`,
    `# hPCC_in,${series.hPCC}`,
    `# fcPCC_psi (=(MR/9.5)^2),${series.fcPCC}`,
    `# c1,${k.c1}`,
    `# c2,${k.c2}`,
    `# c3,${k.c3}`,
    `# c4,${k.c4}`,
    `# IRI_initial,${k.iriInitial}`,
    `# fiCoeff,${k.fiCoeff}`,
    `# p200,${k.p200}`,
    `# acPCC,${k.acPCC}`,
    `# preform,${k.preform}`,
    `# wcPCC,${k.wcPCC}`,
  ].join('\n');

  const header = [
    'age_yr',
    'crack50_pct', 'crackRel_pct',
    'fault50_in', 'faultRel_in',
    'SCF', 'SPALL_pct', 'SF',
    'TFAULT50_in_mi', 'TFAULTrel_in_mi',
    'c_initial', 'c_crack', 'c_spall', 'c_fault', 'c_sf',
    'IRI50_in_mi', 'IRIrel_in_mi',
  ].join(',');

  const rows = series.years.map(y => [
    y.age,
    y.crack50, y.crackRel,
    y.fault50, y.faultRel,
    y.scf, y.spall, y.sf,
    y.tfault50, y.tfaultRel,
    y.cInitial, y.cCrack, y.cSpall, y.cFault, y.cSF,
    y.iri50, y.iriRel,
  ].join(','));

  return `${meta}\n${header}\n${rows.join('\n')}\n`;
}

/** Triggers a browser download of the verification CSV. Phase 1 only. */
export function downloadIriCsv(series: IriSeries, filename = 'pittrigid_iri_check.csv'): void {
  const blob = new Blob([buildIriCsv(series)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
